import React from "react";
import { Box, Button, Input, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { FiTrash2, FiUser } from "react-icons/fi";
import { LuChevronDown } from "react-icons/lu";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePerfilSettings } from "features/perfil/settings/store";
import { useCreateHijo, useDeleteHijo, useMisHijos, useUpdateHijo } from "features/perfil/hijos/hooks";
import FamiliaEditorLayout, { EditorField, editorInputProps } from "./FamiliaEditorLayout";
import RedirectStatusPage from "../../main/Status/RedirectStatusPage";

const MotionBox = motion.create(Box);
const GRADES = [
  { value: 2015, label: "4to" }, { value: 2014, label: "5to" },
  { value: 2013, label: "6to" }, { value: 2012, label: "7mo" },
  { value: 2011, label: "8vo" }, { value: 2010, label: "9no" },
];
const EMPTY = { nombre: "", apellidoPaterno: "", apellidoMaterno: "", fechaNacimiento: "", generacion: 2015, campusId: "", colegio: "" };

function GradePicker({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const selected = GRADES.find((grade) => grade.value === Number(value)) || GRADES[0];
  return <EditorField label="Grado actual">
    <Box position="relative">
      <Button w="full" h="54px" px="4" rounded="14px" bg="gray.50" justifyContent="space-between" fontWeight="700" onClick={() => setOpen((current) => !current)} aria-expanded={open}>{selected.label}<LuChevronDown style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s" }}/></Button>
      <AnimatePresence>{open && <MotionBox mt="2" p="2" rounded="18px" borderWidth="1px" borderColor="gray.200" bg="white" boxShadow="lg" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} position="absolute" top="100%" left="0" right="0" zIndex="3">
        <SimpleGrid columns="3" gap="2">{GRADES.map((grade) => {
          const active = grade.value === Number(value);
          return <Button key={grade.value} h="46px" rounded="13px" bg={active ? "red.50" : "white"} color={active ? "brand.red" : "gray.700"} borderWidth="1px" borderColor={active ? "red.200" : "gray.100"} onClick={() => { onChange(grade.value); setOpen(false); }}>{grade.label}</Button>;
        })}</SimpleGrid>
      </MotionBox>}</AnimatePresence>
    </Box>
  </EditorField>;
}

export default function EditarHijoPerfil() {
  const { hijoId } = useParams();
  const navigate = useNavigate();
  const perfil = usePerfilSettings();
  const creating = hijoId === "nuevo";
  const { data: children = [], isLoading: childrenLoading } = useMisHijos(perfil.ready);
  const createChild = useCreateHijo();
  const updateChild = useUpdateHijo();
  const deleteChild = useDeleteHijo();
  const [form, setForm] = React.useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [error, setError] = React.useState("");
  const initialized = React.useRef(false);
  const child = children.find((item) => item.id === hijoId);
  const loading = !perfil.ready || childrenLoading;
  const saving = createChild.isPending || updateChild.isPending || deleteChild.isPending;

  React.useEffect(() => {
    if (loading || initialized.current) return;
    if (creating) {
      setForm({
        ...EMPTY,
        apellidoPaterno: perfil.familia?.apellidoPaternoPapa || "",
        apellidoMaterno: perfil.familia?.apellidoPaternoMama || "",
        campusId: perfil.familia?.campusId || children[0]?.campusId || "",
        colegio: perfil.familia?.colegio || children[0]?.colegio || "",
      });
    } else if (child) {
      setForm({
        nombre: child.nombre || "", apellidoPaterno: child.apellidoPaterno || "", apellidoMaterno: child.apellidoMaterno || "",
        fechaNacimiento: child.fechaNacimiento || "", generacion: Number(child.generacion), campusId: perfil.familia?.campusId || child.campusId || "", colegio: perfil.familia?.colegio || child.colegio || "",
      });
    }
    initialized.current = true;
  }, [loading, creating, child, children, perfil.familia]);

  if (loading) return <Box minH="100dvh" display="grid" placeItems="center"><Spinner color="brand.red"/></Box>;
  if (!creating && !child) return <RedirectStatusPage to="/perfil/familia" eyebrow="Perfil no encontrado" title="No encontramos a este integrante" description="Es posible que la información haya cambiado. Volveremos al resumen de tu familia." />;

  const update = (field, value) => { setError(""); setForm((current) => ({ ...current, [field]: value })); };
  const valid = form.nombre.trim().length >= 2 && form.apellidoPaterno.trim() && form.apellidoMaterno.trim() && form.fechaNacimiento && form.generacion && form.campusId && form.colegio.trim();
  const save = async () => {
    const payload = {
      ...form,
      nombre: form.nombre.trim(), apellidoPaterno: form.apellidoPaterno.trim(), apellidoMaterno: form.apellidoMaterno.trim(), colegio: form.colegio.trim(), generacion: Number(form.generacion),
    };
    try {
      if (creating) await createChild.mutateAsync(payload);
      else await updateChild.mutateAsync({ id: hijoId, patch: payload });
      await perfil.invalidateHijos();
      navigate("/perfil/familia", { replace: true });
    } catch { setError("No pudimos guardar los cambios. Revisa la informaci\u00f3n."); }
  };
  const remove = async () => {
    try {
      await deleteChild.mutateAsync(hijoId);
      await perfil.invalidateHijos();
      navigate("/perfil/familia", { replace: true });
    } catch { setError("No pudimos eliminar este registro."); }
  };

  return <FamiliaEditorLayout title={creating ? "Agregar hijo" : "Editar hijo"} description="Datos personales" icon={<FiUser/>} iconBg="green.50" iconColor="green.600" onSave={save} saveLabel={creating ? "Guardar hijo" : "Guardar cambios"} disabled={!valid} busy={saving}>
    <Stack gap="6" maxW="570px">
      {(!form.campusId || !form.colegio) && <Box p="4" rounded="18px" bg="orange.50" color="orange.800"><Text fontWeight="800">Faltan los datos escolares familiares</Text><Text mt="1" fontSize="sm">Configura campus y colegio una sola vez antes de guardar.</Text><Button asChild mt="3" size="sm" rounded="full" bg="orange.500" color="white"><Link to="/perfil/familia/escolar">Configurar ahora</Link></Button></Box>}
      <EditorField label="Nombre(s)"><Input {...editorInputProps} autoComplete="given-name" value={form.nombre} onChange={(event) => update("nombre", event.target.value)}/></EditorField>
      <SimpleGrid columns={2} gap="3">
        <EditorField label="Apellido paterno" hint={creating ? "De papá" : undefined}><Input {...editorInputProps} autoComplete="family-name" value={form.apellidoPaterno} onChange={(event) => update("apellidoPaterno", event.target.value)}/></EditorField>
        <EditorField label="Apellido materno" hint={creating ? "De mamá" : undefined}><Input {...editorInputProps} value={form.apellidoMaterno} onChange={(event) => update("apellidoMaterno", event.target.value)}/></EditorField>
      </SimpleGrid>
      <EditorField label="Fecha de nacimiento"><Input {...editorInputProps} type="date" value={form.fechaNacimiento} onChange={(event) => update("fechaNacimiento", event.target.value)}/></EditorField>
      <GradePicker value={form.generacion} onChange={(value) => update("generacion", value)}/>

      {error && <Text color="red.600" fontSize="sm">{error}</Text>}
      {!creating && <Box pt="3" borderTopWidth="1px" borderColor="gray.100">
        {!confirmDelete ? <Button variant="ghost" color="red.600" px="0" onClick={() => setConfirmDelete(true)}><FiTrash2/>Eliminar hijo</Button> : <Box p="4" rounded="18px" bg="red.50"><Text fontWeight="800" color="red.800">{"\u00bfEliminar este hijo?"}</Text><Text mt="1" fontSize="sm" color="red.700">{"Esta acci\u00f3n elimina tambi\u00e9n sus registros relacionados."}</Text><Stack mt="4" direction="row"><Button flex="1" variant="outline" rounded="full" onClick={() => setConfirmDelete(false)}>Cancelar</Button><Button flex="1" bg="red.600" color="white" rounded="full" onClick={remove} disabled={saving}>{saving ? <Spinner size="sm"/> : "Eliminar"}</Button></Stack></Box>}
      </Box>}
    </Stack>
  </FamiliaEditorLayout>;
}
